import { useState } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@services/auth';
import { ToastViewport } from '@shared/ui';
import { ErrorBoundary } from './ErrorBoundary';
import { env } from '@config/env';

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Sensible defaults for a dashboard:
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (failureCount, error) => {
          // Don't retry on 4xx auth/validation errors. TanStack types `error`
          // as `Error`, so we only need to check for a numeric `status` field.
          const status =
            'status' in error && typeof error.status === 'number' ? error.status : undefined;
          if (status !== undefined && status >= 400 && status < 500) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: env.VITE_APP_ENV === 'production',
      },
      mutations: { retry: 0 },
    },
  });
}

export function Providers({ children }: PropsWithChildren): ReactElement {
  // Lazy-init via useState so the client is created exactly once per mount
  // (and is reset cleanly in tests that re-render the tree).
  const [client] = useState(makeQueryClient);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={client}>
        <AuthProvider>
          {children}
          <ToastViewport />
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
